import { useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle,
  Banknote,
  Building2,
  ChevronDown,
  Clock3,
  Coins,
  ExternalLink,
  FileWarning,
  Percent,
  Receipt,
  ShieldCheck,
  Tag,
  type LucideIcon,
} from "lucide-react";
import {
  COVER_TYPE_BY_KEY,
  type BaseQuote,
  type CoverKey,
  type FinanceCost,
  type FinanceOptions,
} from "@ir/types";
import {
  calculateCustomerPricing,
  formatCurrency,
  formatDateTime,
  formatPercent,
  getProviderAnnualPremium,
  hasCustomerFacingQuotes,
  htmlListToPlainText,
} from "@ir/services";
import {
  Alert,
  Badge,
  Card,
  CardHeader,
  CopyButton,
  CopyableId,
  DetailList,
  EmptyState,
  StatusPill,
  Tooltip,
  cn,
  isString,
  useCollapsible,
  usePersistentState,
  type BadgeTone,
} from "@ir/ui";

const COVER_ORDER: CoverKey[] = ["comprehensive", "tpft", "tp"];
const EXPECTED_PRICING_PROVIDERS = ["granite", "inshur", "kinetiq"] as const;

/* -------------------------------------------------------------------------- */
/* Per-provider summary                                                        */
/* -------------------------------------------------------------------------- */

/**
 * One entry in the provider rail — either a provider that answered, or one that
 * was expected and did not. Missing providers are shown as tabs rather than
 * hidden, because "Kinetiq never replied" is itself an answer the operator
 * needs, and burying it costs more than the row it occupies.
 */
type ProviderEntry =
  | { kind: "quote"; id: string; provider: string; baseQuote: BaseQuote }
  | { kind: "missing"; id: string; provider: string };

interface ProviderSummary {
  name: string;
  insurer?: string | null;
  statusLabel: string;
  tone: BadgeTone;
  /** Cheapest customer-facing selling price this provider offers, if any. */
  bestSelling: number | null;
  bestCoverLabel: string | null;
  quoteCount: number;
  hasFailure: boolean;
}

/** Cheapest selling price across every cover option and broker quote returned. */
function bestSellingPrice(
  baseQuote: BaseQuote,
): { amount: number; coverLabel: string } | null {
  const provider = baseQuote.provider ?? "";
  let best: { amount: number; coverLabel: string } | null = null;

  const consider = (premium: number, coverLabel: string) => {
    if (!(premium > 0)) return;
    const selling = calculateCustomerPricing(provider, premium).totalSellingPrice;
    if (!best || selling < best.amount) best = { amount: selling, coverLabel };
  };

  const cover = baseQuote.non_broker_quote_details?.cover;
  if (cover) {
    for (const coverKey of COVER_ORDER) {
      const annually = getProviderAnnualPremium(provider, cover[coverKey]);
      consider(annually, COVER_TYPE_BY_KEY[coverKey]);
    }
  }

  for (const broker of baseQuote.broker_quote_details ?? []) {
    if (broker.is_decline || !broker.premium) continue;
    consider(
      normalizeBrokerPremium(provider, broker.premium),
      broker.cover_type ?? "Broker quote",
    );
  }

  return best;
}

function countQuoteOptions(baseQuote: BaseQuote): number {
  const cover = baseQuote.non_broker_quote_details?.cover;
  const coverCount = cover
    ? COVER_ORDER.filter((key) => getProviderAnnualPremium(baseQuote.provider, cover[key]) > 0).length
    : 0;
  const brokerCount = (baseQuote.broker_quote_details ?? []).filter(
    (broker) => !broker.is_decline && (broker.premium ?? 0) > 0,
  ).length;
  return coverCount + brokerCount;
}

function summariseEntry(
  entry: ProviderEntry,
  reconciliationDeadlineAt?: string | null,
  reconciliationProcessedAt?: string | null,
): ProviderSummary {
  const name = formatProviderName(entry.provider);

  if (entry.kind === "missing") {
    const deadline = reconciliationDeadlineAt ? new Date(reconciliationDeadlineAt) : null;
    const elapsed = Boolean(
      deadline && !Number.isNaN(deadline.getTime()) && deadline.getTime() <= Date.now(),
    );
    const timedOut = Boolean(reconciliationProcessedAt) || elapsed;
    return {
      name,
      statusLabel: timedOut ? "Timed out" : "Awaiting",
      tone: timedOut ? "warning" : "neutral",
      bestSelling: null,
      bestCoverLabel: null,
      quoteCount: 0,
      hasFailure: false,
    };
  }

  const { baseQuote } = entry;
  const best = bestSellingPrice(baseQuote);
  const hasFailure = getFailureDetails(baseQuote).length > 0;
  const statusLabel = getProviderDisplayStatus(baseQuote)?.trim() || "Unknown";

  // Priced beats everything: a provider that returned a number is useful even
  // if one of its cover types also carried a refer reason.
  const tone: BadgeTone = best ? "success" : hasFailure ? "danger" : "neutral";

  return {
    name,
    insurer: baseQuote.non_broker_quote_details?.insurer,
    statusLabel,
    tone,
    bestSelling: best?.amount ?? null,
    bestCoverLabel: best?.coverLabel ?? null,
    quoteCount: countQuoteOptions(baseQuote),
    hasFailure,
  };
}

/* -------------------------------------------------------------------------- */
/* Panel                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Everything every provider returned for one quote.
 *
 * Providers used to stack vertically, so comparing Granite against Kinetiq meant
 * scrolling past a full pricing matrix to reach the next name. Here the
 * providers become a rail — vertical from `lg`, a horizontal scroller below it —
 * and only the selected one renders. The rail carries each provider's status and
 * cheapest selling price, so the comparison an operator actually makes is
 * answered without opening anything.
 */
export function ProviderQuotesPanel({
  baseQuotes,
  reconciliationDeadlineAt,
  reconciliationProcessedAt,
}: {
  baseQuotes: BaseQuote[];
  reconciliationDeadlineAt?: string | null;
  reconciliationProcessedAt?: string | null;
}) {
  const hasReconciliationContext = Boolean(
    reconciliationDeadlineAt || reconciliationProcessedAt,
  );

  const entries = useMemo<ProviderEntry[]>(() => {
    const returned: ProviderEntry[] = baseQuotes.map((baseQuote, index) => ({
      kind: "quote",
      id: baseQuote.id ?? `quote-${index}`,
      provider: baseQuote.provider ?? "",
      baseQuote,
    }));

    if (!hasReconciliationContext) return returned;

    const seen = new Set(
      baseQuotes.map((quote) => (quote.provider ?? "").trim().toLowerCase()),
    );
    const missing: ProviderEntry[] = EXPECTED_PRICING_PROVIDERS.filter(
      (provider) => !seen.has(provider),
    ).map((provider) => ({ kind: "missing", id: `missing-${provider}`, provider }));

    return [...returned, ...missing];
  }, [baseQuotes, hasReconciliationContext]);

  const summaries = useMemo(
    () =>
      new Map(
        entries.map((entry) => [
          entry.id,
          summariseEntry(entry, reconciliationDeadlineAt, reconciliationProcessedAt),
        ]),
      ),
    [entries, reconciliationDeadlineAt, reconciliationProcessedAt],
  );

  // Default to the cheapest priced provider — the one an operator opens first.
  const defaultId = useMemo(() => {
    let cheapest: { id: string; amount: number } | null = null;
    for (const entry of entries) {
      const amount = summaries.get(entry.id)?.bestSelling;
      if (amount === null || amount === undefined) continue;
      if (!cheapest || amount < cheapest.amount) cheapest = { id: entry.id, amount };
    }
    return cheapest?.id ?? entries[0]?.id ?? null;
  }, [entries, summaries]);

  // Stored as the provider slug rather than the base-quote id: ids are unique
  // per quote, so remembering one would never match the next quote opened.
  // "I was looking at Kinetiq" survives both a refresh and a different record.
  const [rememberedProvider, setRememberedProvider] = usePersistentState(
    "ir-admin-quote-provider",
    "",
    isString,
  );

  const activeEntry = useMemo(() => {
    const remembered = entries.find(
      (entry) => entry.provider.trim().toLowerCase() === rememberedProvider,
    );
    return remembered ?? entries.find((entry) => entry.id === defaultId) ?? null;
  }, [entries, rememberedProvider, defaultId]);

  const activeId = activeEntry?.id ?? null;

  if (entries.length === 0) {
    return (
      <Card padded={false}>
        <EmptyState
          icon={FileWarning}
          title="No quotes received yet"
          description="The provider services have not returned prices for this risk. They usually arrive within a few minutes of submission."
        />
      </Card>
    );
  }

  const priced = entries.filter(
    (entry) => summaries.get(entry.id)?.bestSelling !== null,
  );
  const cheapest = priced.reduce<{ id: string; amount: number } | null>(
    (lowest, entry) => {
      const amount = summaries.get(entry.id)!.bestSelling!;
      return !lowest || amount < lowest.amount ? { id: entry.id, amount } : lowest;
    },
    null,
  );

  return (
    <div className="space-y-4">
      <MarketSummary
        total={entries.length}
        pricedCount={priced.length}
        cheapest={
          cheapest
            ? {
                amount: cheapest.amount,
                provider: summaries.get(cheapest.id)!.name,
                cover: summaries.get(cheapest.id)!.bestCoverLabel,
              }
            : null
        }
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(14rem,17rem)_minmax(0,1fr)] lg:items-start">
        {/* Vertical rail from lg; horizontal scroller below it. */}
        <div
          role="tablist"
          aria-label="Providers"
          aria-orientation="vertical"
          className={cn(
            "no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0",
            "lg:sticky lg:top-20 lg:flex-col lg:overflow-visible lg:pb-0",
          )}
        >
          {entries.map((entry) => (
            <ProviderTab
              key={entry.id}
              summary={summaries.get(entry.id)!}
              selected={entry.id === activeId}
              isCheapest={cheapest?.id === entry.id && priced.length > 1}
              onSelect={() => setRememberedProvider(entry.provider.trim().toLowerCase())}
            />
          ))}
        </div>

        <div className="min-w-0">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeId ?? "none"}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
              role="tabpanel"
            >
              {activeEntry?.kind === "quote" && (
                <ProviderDetail
                  baseQuote={activeEntry.baseQuote}
                  summary={summaries.get(activeEntry.id)!}
                  reconciliationDeadlineAt={reconciliationDeadlineAt}
                />
              )}
              {activeEntry?.kind === "missing" && (
                <MissingProviderCard
                  provider={activeEntry.provider}
                  reconciliationDeadlineAt={reconciliationDeadlineAt}
                  reconciliationProcessedAt={reconciliationProcessedAt}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

/** One-line answer to "did we get prices, and what is the best one?" */
function MarketSummary({
  total,
  pricedCount,
  cheapest,
}: {
  total: number;
  pricedCount: number;
  cheapest: { amount: number; provider: string; cover: string | null } | null;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-line bg-surface px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2.5 text-sm">
        <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-gold-100 text-gold-700 dark:bg-gold-950/60 dark:text-gold-400">
          <Building2 size={16} aria-hidden="true" />
        </span>
        <span>
          <span className="font-medium text-content">
            {pricedCount} of {total} provider{total === 1 ? "" : "s"} priced
          </span>
          <span className="block text-xs text-content-subtle">
            Select a provider to see its full response
          </span>
        </span>
      </div>

      {cheapest && (
        <div className="flex items-center gap-2.5 rounded-lg border border-gold-300/60 bg-gold-50 px-3 py-2 dark:border-gold-800 dark:bg-gold-950/40">
          <Tag size={15} aria-hidden="true" className="shrink-0 text-gold-600" />
          <span className="text-sm">
            <span className="font-semibold tabular text-content">
              {formatCurrency(cheapest.amount)}
            </span>
            <span className="ml-1.5 text-xs text-content-muted">
              cheapest · {cheapest.provider}
              {cheapest.cover ? ` · ${cheapest.cover}` : ""}
            </span>
          </span>
        </div>
      )}
    </div>
  );
}

const TONE_DOT: Record<BadgeTone, string> = {
  neutral: "bg-ink-400",
  gold: "bg-gold-500",
  crimson: "bg-crimson-600",
  success: "bg-success-500",
  warning: "bg-warning-500",
  danger: "bg-danger-500",
  info: "bg-info-500",
};

function ProviderTab({
  summary,
  selected,
  isCheapest,
  onSelect,
}: {
  summary: ProviderSummary;
  selected: boolean;
  isCheapest: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      onClick={onSelect}
      className={cn(
        "relative shrink-0 rounded-xl border px-3 py-2.5 text-left transition-all duration-200",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500",
        "min-w-[11rem] lg:w-full lg:min-w-0",
        selected
          ? "border-gold-400 bg-gold-50 shadow-ir-sm dark:border-gold-700 dark:bg-gold-950/40"
          : "border-line bg-surface hover:border-gold-300 hover:bg-surface-sunken dark:hover:border-gold-800",
      )}
    >
      {selected && (
        <motion.span
          layoutId="provider-tab-rail"
          aria-hidden="true"
          className="absolute inset-y-2 left-0 w-0.5 rounded-full bg-gold-sheen"
          transition={{ type: "spring", stiffness: 420, damping: 34 }}
        />
      )}

      <span className="flex items-center gap-2">
        <span
          aria-hidden="true"
          className={cn("size-1.5 shrink-0 rounded-full", TONE_DOT[summary.tone])}
        />
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-content">
          {summary.name}
        </span>
        {isCheapest && (
          <Tooltip label="Cheapest quote">
            {/*
              With the price gone from the rail this icon is the only thing
              marking the cheapest provider, so it needs a name of its own — it
              can no longer borrow meaning from a figure sitting beside it.
            */}
            <span className="flex shrink-0 items-center text-gold-600">
              <Tag size={12} aria-hidden="true" />
              <span className="sr-only">Cheapest quote</span>
            </span>
          </Tooltip>
        )}
      </span>

      {/*
        Deliberately no price here. A figure in the rail invites someone to quote
        it without opening the provider, where the cover type, excess and
        conditions that qualify it live. The cheapest tag still marks the best
        one, so the comparison survives without the number standing in for it.
      */}
      <span className="mt-1 flex items-baseline justify-between gap-2">
        <span className="truncate text-xs text-content-subtle">
          {summary.statusLabel}
        </span>
        {summary.quoteCount > 0 ? (
          <span className="shrink-0 text-xs font-medium text-content-muted">
            {summary.quoteCount} option{summary.quoteCount === 1 ? "" : "s"}
          </span>
        ) : (
          <span className="shrink-0 text-xs text-content-subtle">—</span>
        )}
      </span>

      {summary.insurer && (
        <span className="mt-0.5 block truncate text-[11px] text-content-subtle">
          {summary.insurer}
        </span>
      )}
    </button>
  );
}

function MissingProviderCard({
  provider,
  reconciliationDeadlineAt,
  reconciliationProcessedAt,
}: {
  provider: string;
  reconciliationDeadlineAt?: string | null;
  reconciliationProcessedAt?: string | null;
}) {
  const deadline = reconciliationDeadlineAt ? new Date(reconciliationDeadlineAt) : null;
  const deadlineElapsed = Boolean(
    deadline && !Number.isNaN(deadline.getTime()) && deadline.getTime() <= Date.now(),
  );
  const timedOut = Boolean(reconciliationProcessedAt) || deadlineElapsed;

  return (
    <Card>
      <CardHeader
        icon={Building2}
        title={<span>{formatProviderName(provider)}</span>}
        actions={<StatusPill status={timedOut ? "Timed out" : "Awaiting response"} />}
      />
      <Alert
        tone={timedOut ? "warning" : "info"}
        title={timedOut ? "No response by deadline" : "Awaiting provider response"}
      >
        {timedOut
          ? "No provider outcome was stored before the marketplace reconciliation deadline. This is a marketplace timeout, not a fabricated provider failure."
          : `The marketplace is still waiting for this provider. The reconciliation deadline is ${formatDateTime(reconciliationDeadlineAt)}.`}
      </Alert>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Detail                                                                      */
/* -------------------------------------------------------------------------- */

/** Headline figures, so the answer is readable before anything is expanded. */
function HeadlineTiles({
  summary,
  excess,
  providerPremium,
}: {
  summary: ProviderSummary;
  excess?: number;
  providerPremium: number | null;
}) {
  const tiles = [
    {
      icon: Tag,
      label: "Best selling price",
      value:
        summary.bestSelling !== null ? formatCurrency(summary.bestSelling) : "No price",
      hint: summary.bestCoverLabel ?? undefined,
      emphasis: true,
    },
    {
      icon: Banknote,
      label: "Provider premium",
      value: providerPremium !== null ? formatCurrency(providerPremium) : "—",
      hint: "before fees",
    },
    {
      icon: ShieldCheck,
      label: "Excess",
      value: excess !== undefined ? formatCurrency(excess) : "—",
    },
    {
      icon: Clock3,
      label: "Status",
      value: summary.statusLabel,
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-2 xl:grid-cols-4">
      {tiles.map((tile) => (
        <div
          key={tile.label}
          className={cn(
            "rounded-lg border px-3 py-2.5",
            tile.emphasis
              ? "border-gold-300/60 bg-gold-50 dark:border-gold-800 dark:bg-gold-950/40"
              : "border-line bg-surface-sunken",
          )}
        >
          <span className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-content-subtle">
            <tile.icon size={11} aria-hidden="true" />
            {tile.label}
          </span>
          <span
            className={cn(
              "mt-1 block truncate font-semibold tabular text-content",
              tile.emphasis ? "text-xl" : "text-sm",
            )}
            title={typeof tile.value === "string" ? tile.value : undefined}
          >
            {tile.value}
          </span>
          {tile.hint && (
            <span className="block truncate text-[11px] text-content-subtle">
              {tile.hint}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

/** Collapsed-by-default block for the long, rarely-needed metadata. */
function Collapsible({
  title,
  icon: Icon,
  storageKey,
  defaultOpen = false,
  children,
}: {
  title: string;
  icon: LucideIcon;
  /** Stable across quotes, so the preference is about the section not the record. */
  storageKey: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const { open, toggle } = useCollapsible(storageKey, defaultOpen);

  return (
    <div className="overflow-hidden rounded-xl border border-line">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 bg-surface-sunken px-4 py-2.5 text-left transition-colors hover:bg-gold-50 dark:hover:bg-gold-950/30"
      >
        <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-content-subtle">
          <Icon size={12} aria-hidden="true" />
          {title}
        </span>
        <ChevronDown
          size={15}
          aria-hidden="true"
          className={cn(
            "shrink-0 text-content-subtle transition-transform duration-200",
            open && "rotate-180",
          )}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="px-4 py-3">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ProviderDetail({
  baseQuote,
  summary,
  reconciliationDeadlineAt,
}: {
  baseQuote: BaseQuote;
  summary: ProviderSummary;
  reconciliationDeadlineAt?: string | null;
}) {
  const nonBroker = baseQuote.non_broker_quote_details;
  // Memoised because `?? []` would otherwise hand a new array to the pricing
  // useMemo below on every render, defeating it entirely.
  const brokerQuotes = useMemo(
    () => baseQuote.broker_quote_details ?? [],
    [baseQuote.broker_quote_details],
  );
  const isOpenGi = (baseQuote.provider ?? "").toLowerCase() === "opengi";
  const ogiMetadata = baseQuote.provider_metadata?.ogi;
  const hasSuccessfulOgiOutcome = isSuccessfulOgiOutcome(baseQuote);
  const failureDetails = getFailureDetails(baseQuote);
  const technicalFailureDetails = getTechnicalFailureDetails(baseQuote);
  const lateResponseAt = getLateResponseAt(baseQuote, reconciliationDeadlineAt);
  const quoteLink = brokerQuotes[0]?.info ? extractFirstLink(brokerQuotes[0].info) : null;

  // The premium behind the cheapest selling price, for the headline tile.
  const providerPremium = useMemo(() => {
    const cover = nonBroker?.cover;
    const values: number[] = [];
    if (cover) {
      for (const key of COVER_ORDER) {
        const annually = getProviderAnnualPremium(baseQuote.provider, cover[key]);
        if (annually > 0) values.push(annually);
      }
    }
    for (const broker of brokerQuotes) {
      if (broker.is_decline || !broker.premium) continue;
      values.push(normalizeBrokerPremium(baseQuote.provider, broker.premium));
    }
    return values.length ? Math.min(...values) : null;
  }, [baseQuote.provider, brokerQuotes, nonBroker?.cover]);

  const headlineExcess = useMemo(() => {
    const excess = nonBroker?.excess;
    if (excess) {
      const bestCoverKey = COVER_ORDER.find(
        (key) => COVER_TYPE_BY_KEY[key] === summary.bestCoverLabel,
      );
      if (bestCoverKey) return excess[bestCoverKey];
    }

    const bestBroker = brokerQuotes.find(
      (broker) =>
        !broker.is_decline &&
        (broker.premium ?? 0) > 0 &&
        broker.cover_type === summary.bestCoverLabel,
    );
    return bestBroker?.voluntary_excess ?? undefined;
  }, [brokerQuotes, nonBroker?.excess, summary.bestCoverLabel]);

  return (
    <Card>
      {/*
        A bespoke header rather than CardHeader: two status badges plus a
        provider name do not fit on one row at phone width, and CardHeader keeps
        them side by side, which truncates the name to "Grani…". Here the badges
        drop below the name until there is room for both.
      */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <span className="mt-0.5 grid size-9 shrink-0 place-items-center rounded-lg bg-gold-100 text-gold-700 dark:bg-gold-950/60 dark:text-gold-400">
            <Building2 size={17} aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-content">{summary.name}</h3>
            {nonBroker?.insurer && (
              <p className="mt-0.5 text-sm text-content-muted">{nonBroker.insurer}</p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          {lateResponseAt && <Badge tone="warning">Late response</Badge>}
          <StatusPill status={summary.statusLabel} />
        </div>
      </div>

      <HeadlineTiles
        summary={summary}
        excess={headlineExcess}
        providerPremium={providerPremium}
      />

      {failureDetails.length > 0 && summary.quoteCount === 0 && (
        <Alert tone="warning" title="Failure details" className="mt-4">
          <ul className="list-disc space-y-1 pl-4">
            {failureDetails.map((detail) => (
              <li key={detail}>{detail}</li>
            ))}
          </ul>
          {technicalFailureDetails.length > 0 && (
            <details className="mt-3 text-xs text-content-subtle">
              <summary className="cursor-pointer font-medium">Show technical details</summary>
              <ul className="mt-2 list-disc space-y-1 pl-4">
                {technicalFailureDetails.map((detail) => (
                  <li key={detail} className="break-words">{detail}</li>
                ))}
              </ul>
            </details>
          )}
        </Alert>
      )}

      {lateResponseAt && (
        <Alert tone="info" title="Stored after reconciliation deadline" className="mt-4">
          This provider outcome was stored at {formatDateTime(lateResponseAt)}. Customer
          notifications remain one-time, so it did not trigger another email or SMS.
        </Alert>
      )}

      {quoteLink && (
        <a
          href={quoteLink}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-flex items-center gap-1.5 text-sm text-gold-700 underline underline-offset-2 dark:text-gold-400"
        >
          Open provider link <ExternalLink size={13} aria-hidden="true" />
        </a>
      )}

      {nonBroker?.cover && (
        <div className="mt-5 space-y-3">
          <h4 className="text-xs font-semibold uppercase tracking-[0.08em] text-content-subtle">
            Cover options
          </h4>
          {COVER_ORDER.map((coverKey) => {
            const options = nonBroker.cover?.[coverKey];
            if (!options) return null;
            return (
              <CoverBlock
                key={coverKey}
                provider={baseQuote.provider}
                coverKey={coverKey}
                options={options}
                excess={nonBroker.excess?.[coverKey]}
              />
            );
          })}
        </div>
      )}

      {brokerQuotes.length > 0 && (
        <div className="mt-5">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-content-subtle">
            Broker quotes
          </h4>
          <div className="space-y-2">
            {brokerQuotes.map((broker, index) => (
              <div
                key={broker.uuid ?? index}
                className="rounded-lg border border-line bg-surface-sunken p-3"
              >
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <StatusPill status={broker.state} />
                  {broker.is_decline && <Badge tone="danger">Declined</Badge>}
                  {broker.is_refer && <Badge tone="warning">Referred</Badge>}
                  {broker.cover_type && <Badge tone="gold">{broker.cover_type}</Badge>}
                </div>
                <DetailList
                  items={[
                    {
                      label: "Reference",
                      value: broker.uuid ? (
                        <CopyableId value={broker.uuid} truncate={false} />
                      ) : (
                        ""
                      ),
                      hideWhenEmpty: true,
                    },
                    {
                      label: "Provider premium",
                      value: broker.premium
                        ? formatCurrency(
                            normalizeBrokerPremium(baseQuote.provider, broker.premium),
                          )
                        : "",
                      hideWhenEmpty: true,
                    },
                    {
                      label: "Voluntary excess",
                      value:
                        broker.voluntary_excess !== null &&
                        broker.voluntary_excess !== undefined
                          ? formatCurrency(broker.voluntary_excess)
                          : "",
                      hideWhenEmpty: true,
                    },
                    {
                      label: "Compulsory excess",
                      value: broker.compulsory_excess?.join(", "),
                      hideWhenEmpty: true,
                    },
                    {
                      label: "Valid until",
                      value: formatDateTime(broker.expiration_date, ""),
                      hideWhenEmpty: true,
                    },
                    {
                      label: "Decline reason",
                      value: htmlListToPlainText(broker.decline_reason).join(", "),
                      hideWhenEmpty: true,
                      span: 2,
                    },
                    {
                      label: "Refer reason",
                      value: htmlListToPlainText(broker.refer_reason).join(", "),
                      hideWhenEmpty: true,
                      span: 2,
                    },
                    // Emptiness is decided here, not by DetailList: it can only
                    // test the value it is handed, and a React element is never
                    // "empty" to it — which left blank Endorsements and
                    // Conditions rows on providers that return neither.
                    ...buildTermItems("Endorsements", broker.endorsements),
                    ...buildTermItems("Conditions", broker.conditions),
                  ]}
                />
                {!broker.is_decline && broker.premium ? (
                  <CustomerPricingSummary
                    provider={baseQuote.provider}
                    premium={normalizeBrokerPremium(baseQuote.provider, broker.premium)}
                    className="mt-3"
                  />
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* References and provider metadata are reference material, not the answer. */}
      <div className="mt-5 space-y-2">
        <Collapsible
          title="References and status"
          icon={Receipt}
          storageKey="provider-references"
          defaultOpen
        >
          <DetailList
            layout="stacked"
            items={[
              {
                label: isOpenGi ? "OGI quote reference" : "Reference",
                value: baseQuote.ref ? <ReferenceValue value={baseQuote.ref} /> : "",
              },
              {
                label: isOpenGi ? "Broker policy reference" : "Online reference",
                value: baseQuote.online_ref ? (
                  <ReferenceValue value={baseQuote.online_ref} />
                ) : (
                  ""
                ),
                hideWhenEmpty: true,
              },
              { label: "Status", value: nonBroker?.status, hideWhenEmpty: true },
              {
                label: "Business type",
                value: nonBroker?.business_type,
                hideWhenEmpty: true,
              },
              {
                label: "No claims bonus",
                value:
                  nonBroker?.ncb !== null && nonBroker?.ncb !== undefined
                    ? `${nonBroker.ncb} years`
                    : "",
                hideWhenEmpty: true,
              },
              {
                label: "No quote reason",
                value: nonBroker?.no_quote_reason,
                hideWhenEmpty: true,
                span: 2,
              },
            ]}
          />
          {!hasSuccessfulOgiOutcome && technicalFailureDetails.length > 0 && (
            <details className="mt-4 text-xs text-content-subtle">
              <summary className="cursor-pointer font-medium">Show technical details</summary>
              <ul className="mt-2 list-disc space-y-1 pl-4">
                {technicalFailureDetails.map((detail) => (
                  <li key={detail} className="break-words">{detail}</li>
                ))}
              </ul>
            </details>
          )}
        </Collapsible>

        {isOpenGi && ogiMetadata && (
          <Collapsible
            title="OpenGI handoff"
            icon={AlertTriangle}
            storageKey="provider-ogi-handoff"
            defaultOpen
          >
            <DetailList
              layout="stacked"
              items={[
                {
                  label: "OGI prospect reference",
                  value: ogiMetadata.prospect_reference ? (
                    <CopyableId value={ogiMetadata.prospect_reference} truncate={false} />
                  ) : (
                    ""
                  ),
                  hideWhenEmpty: true,
                },
                {
                  label: "Submission path",
                  value: ogiMetadata.successful_path ?? ogiMetadata.submission_mode,
                  hideWhenEmpty: true,
                },
                {
                  label: "OGI quote reference",
                  value: (ogiMetadata.quote_reference ?? baseQuote.ref) ? (
                    <CopyableId
                      value={(ogiMetadata.quote_reference ?? baseQuote.ref)!}
                      truncate={false}
                    />
                  ) : (
                    ""
                  ),
                  hideWhenEmpty: true,
                },
                {
                  label: "Broker policy reference",
                  value: (() => {
                    const reference =
                      ogiMetadata.broker_policy_reference ??
                      ogiMetadata.send_to_broker_ref ??
                      baseQuote.online_ref;
                    return reference ? (
                      <CopyableId value={reference} truncate={false} />
                    ) : (
                      ""
                    );
                  })(),
                  hideWhenEmpty: true,
                },
                {
                  label: "Broker reference",
                  value: ogiMetadata.broker_reference ? (
                    <CopyableId value={ogiMetadata.broker_reference} truncate={false} />
                  ) : (
                    ""
                  ),
                  hideWhenEmpty: true,
                },
                { label: "Select list", value: ogiMetadata.select_list, hideWhenEmpty: true },
                {
                  label: "Active quote call success",
                  value: formatNullableBoolean(ogiMetadata.active_quote_call_success),
                  hideWhenEmpty: true,
                },
                {
                  label: "Send to broker successful",
                  value: formatNullableBoolean(ogiMetadata.send_to_broker_successful),
                  hideWhenEmpty: true,
                },
                {
                  label: "Send to broker description",
                  value: ogiMetadata.send_to_broker_description,
                  hideWhenEmpty: true,
                  span: 2,
                },
                {
                  label: "Send to broker status",
                  value: ogiMetadata.send_to_broker_status,
                  hideWhenEmpty: true,
                },
                {
                  label: "InStream status",
                  value: ogiMetadata.instream_status,
                  hideWhenEmpty: true,
                },
                {
                  label: "InStream error",
                  value: hasSuccessfulOgiOutcome
                    ? undefined
                    : ogiMetadata.instream_error_message,
                  hideWhenEmpty: true,
                  span: 2,
                },
                {
                  label: "Send to broker error",
                  value: hasSuccessfulOgiOutcome
                    ? undefined
                    : ogiMetadata.send_to_broker_error_message ||
                      ogiMetadata.send_to_broker_error_id,
                  hideWhenEmpty: true,
                  span: 2,
                },
              ]}
            />
          </Collapsible>
        )}
      </div>
    </Card>
  );
}

function formatProviderName(provider?: string | null): string {
  const normalized = (provider ?? "").trim().toLowerCase();
  if (normalized === "opengi") return "OpenGI";
  if (!normalized) return "Unknown provider";
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function getLateResponseAt(
  baseQuote: BaseQuote,
  reconciliationDeadlineAt?: string | null,
): string | null {
  if (!reconciliationDeadlineAt) return null;

  const deadline = new Date(reconciliationDeadlineAt);
  const responseAtValue = baseQuote.updated_at ?? baseQuote.created_at;
  if (!responseAtValue) return null;

  const responseAt = new Date(responseAtValue);
  if (Number.isNaN(deadline.getTime()) || Number.isNaN(responseAt.getTime())) return null;

  return responseAt.getTime() > deadline.getTime() ? responseAtValue : null;
}

function getProviderDisplayStatus(baseQuote: BaseQuote): string | null | undefined {
  const normalizedProvider = (baseQuote.provider ?? "").trim().toLowerCase();
  if (normalizedProvider !== "opengi") {
    if (normalizedProvider === "granite" && hasCustomerFacingQuotes(baseQuote)) {
      return "Quoted";
    }

    return baseQuote.state;
  }

  const ogi = baseQuote.provider_metadata?.ogi;
  if (!ogi) {
    return baseQuote.state;
  }

  if (ogi.prospect_reference?.trim()) {
    return "Prospect created";
  }

  if (ogi.send_to_broker_successful === true) {
    return ogi.send_to_broker_description?.trim() || "Delivered Successfully";
  }

  const brokerStatus = ogi.send_to_broker_status?.trim().toUpperCase();
  if (brokerStatus === "Q") {
    return ogi.send_to_broker_description?.trim() || "Queued for Delivery";
  }

  if (brokerStatus === "E" || brokerStatus === "ERROR" || ogi.send_to_broker_error_message) {
    return "Failed";
  }

  return ogi.send_to_broker_description?.trim() || baseQuote.state;
}

function getFailureDetails(baseQuote: BaseQuote): string[] {
  const details = new Set<string>();
  const normalizedProvider = (baseQuote.provider ?? "").trim().toLowerCase();
  const normalizedState = (baseQuote.state ?? "").trim().toLowerCase();
  const nonBroker = baseQuote.non_broker_quote_details;
  const brokerQuotes = baseQuote.broker_quote_details ?? [];

  if (normalizedProvider === "opengi" && isSuccessfulOgiOutcome(baseQuote)) {
    return [];
  }

  if (baseQuote.error?.trim()) {
    details.add(formatProviderError(baseQuote.provider, baseQuote.error));
  }

  if (nonBroker?.no_quote_reason?.trim()) {
    details.add(nonBroker.no_quote_reason.trim());
  }

  if (
    normalizedState &&
    ["failed", "declined", "validation failed", "no quote", "error", "refer", "referred"].includes(
      normalizedState,
    )
  ) {
    details.add(`Provider state: ${baseQuote.state}`);
  }

  for (const broker of brokerQuotes) {
    if (broker.is_decline && broker.decline_reason?.length) {
      details.add(
        `${broker.cover_type ?? "Broker quote"} declined: ${broker.decline_reason.join(", ")}`,
      );
    }

    if (broker.is_refer && broker.refer_reason?.length) {
      details.add(
        `${broker.cover_type ?? "Broker quote"} referred: ${broker.refer_reason.join(", ")}`,
      );
    }

    const validationIssues = extractValidationIssues(broker);
    for (const issue of validationIssues) {
      details.add(issue);
    }
  }

  if (normalizedProvider === "opengi") {
    const ogi = baseQuote.provider_metadata?.ogi;

    if (ogi?.send_to_broker_error_message?.trim()) {
      details.add(`Broker handoff error: ${ogi.send_to_broker_error_message.trim()}`);
    }

    if (ogi?.instream_error_message?.trim()) {
      details.add(`InStream error: ${ogi.instream_error_message.trim()}`);
    }

    if (ogi?.send_to_broker_status?.trim()?.toUpperCase() === "Q") {
      details.add(
        `Broker handoff pending: ${ogi.send_to_broker_description?.trim() || "Queued for delivery."}`,
      );
    }

    for (const quote of ogi?.insurer_quotes ?? []) {
      if ((quote.decision ?? "").trim().toUpperCase() === "REFUSED") {
        const insurer = quote.insurer_name?.trim() || "OGI insurer";
        const reason = quote.reason?.trim() || "No reason returned.";
        details.add(`${insurer} refused: ${reason}`);
      }
    }
  }

  return [...details];
}

function getTechnicalFailureDetails(baseQuote: BaseQuote): string[] {
  const details = new Set<string>();
  if (baseQuote.error?.trim()) details.add(baseQuote.error.trim());
  return [...details];
}

function formatProviderError(provider: string | null | undefined, error: string): string {
  const lower = error.toLowerCase();
  const providerName = formatProviderName(provider ?? "provider");

  if (lower.includes("driverslicense/dln") || lower.includes("driving licence number")) {
    return "The driver's licence number is not in an accepted format. Enter the main driver number without spaces or the separate issue number.";
  }

  if (lower.includes("bodytypecompatiblewithvehicleusecheck")) {
    return "The vehicle body type is not compatible with the selected vehicle use. Please review the vehicle details and try again.";
  }

  if (lower.includes("status\":400") || lower.includes("status:400") || lower.includes("bad request")) {
    return `${providerName} could not process the quote because some submitted details were invalid. Please review the customer and vehicle details and try again.`;
  }

  if (lower.includes("declined")) {
    return `${providerName} declined this quote. No further reason was provided.`;
  }

  if (lower.includes("timeout") || lower.includes("timed out")) {
    return `${providerName} did not respond in time. Please try again.`;
  }

  return `${providerName} could not complete the quote. Please review the submitted details and try again.`;
}

function isSuccessfulOgiOutcome(baseQuote: BaseQuote): boolean {
  const ogi = baseQuote.provider_metadata?.ogi;

  return Boolean(
    ogi?.send_to_broker_successful === true ||
      ogi?.send_to_broker_status?.trim().toUpperCase() === "S" ||
      ogi?.prospect_reference?.trim(),
  );
}

function extractValidationIssues(
  broker: NonNullable<BaseQuote["broker_quote_details"]>[number],
): string[] {
  if (!broker?.info || typeof broker.info !== "object") {
    return [];
  }

  const rawIssues = (broker.info as Record<string, unknown>).ValidationIssues;
  if (!Array.isArray(rawIssues)) {
    return [];
  }

  return rawIssues
    .map((issue) => {
      if (!issue || typeof issue !== "object") {
        return null;
      }

      const message = (issue as Record<string, unknown>).Message;
      return typeof message === "string" && message.trim()
        ? `Validation issue: ${message.trim()}`
        : null;
    })
    .filter((issue): issue is string => issue !== null);
}

function normalizeBrokerPremium(provider: string | null | undefined, premium: number): number {
  const normalizedProvider = (provider ?? "").trim().toLowerCase();

  // INSHUR responses can arrive in minor units, while the admin panel expects pounds.
  if (normalizedProvider === "inshur" && premium > 10000) {
    return premium / 100;
  }

  return premium;
}

function extractFirstLink(info: Record<string, unknown> | null | undefined): string | null {
  if (!info) return null;

  if (typeof info.ReviewLink === "string") return info.ReviewLink;
  if (typeof info.QuoteLink === "string") return info.QuoteLink;

  const quote = info.Quote;
  if (quote && typeof quote === "object" && quote !== null) {
    const quoteLink = (quote as Record<string, unknown>).quoteLink;
    if (typeof quoteLink === "string") return quoteLink;
  }

  return null;
}

function formatNullableBoolean(value: boolean | null | undefined): string {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "";
}

function CoverBlock({
  provider,
  coverKey,
  options,
  excess,
}: {
  provider?: string | null;
  coverKey: CoverKey;
  options: FinanceOptions;
  excess: number | undefined;
}) {
  const providerPremium = getProviderAnnualPremium(provider, options);
  const isGranite = provider?.trim().toLowerCase() === "granite";
  const customerPricing = providerPremium > 0
    ? calculateCustomerPricing(provider ?? "granite", providerPremium)
    : null;

  return (
    <div className="overflow-hidden rounded-xl border border-line">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line bg-surface-sunken px-4 py-2.5">
        <h5 className="text-sm font-semibold text-content">
          {COVER_TYPE_BY_KEY[coverKey]}
        </h5>
        <div className="flex items-center gap-3 text-sm">
          {excess !== undefined && (
            <span className="text-xs text-content-subtle">
              Excess <span className="tabular text-content-muted">{formatCurrency(excess)}</span>
            </span>
          )}
          {providerPremium > 0 ? (
            <span className="text-right">
              <span className="block font-semibold tabular text-content">
                {formatCurrency(customerPricing?.totalSellingPrice ?? providerPremium)}
                <span className="ml-1 text-xs font-normal text-content-subtle">/year</span>
              </span>
              <span className="text-xs text-content-subtle">
                Provider {formatCurrency(providerPremium)}
              </span>
            </span>
          ) : (
            <Badge tone="neutral">No price</Badge>
          )}
        </div>
      </div>

      {providerPremium > 0 && (
        <div className="divide-y divide-line">
          {customerPricing && (
            <div className="px-4 py-3">
              <CustomerPricingSummary provider={provider ?? "granite"} premium={providerPremium} />
            </div>
          )}
          {isGranite && (
            <>
              <PlanRow
                label="11 monthly instalments"
                plan={options.elevenMonths}
                instalments={11}
                provider={provider}
                providerPremium={providerPremium}
                storageKey={`plan-${coverKey}-11`}
              />
              <PlanRow
                label="10 monthly instalments"
                plan={options.tenMonths}
                instalments={10}
                provider={provider}
                providerPremium={providerPremium}
                storageKey={`plan-${coverKey}-10`}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function PlanRow({
  label,
  plan,
  instalments,
  provider,
  providerPremium,
  storageKey,
}: {
  label: string;
  plan: FinanceCost | undefined;
  instalments: 10 | 11;
  provider?: string | null;
  providerPremium: number;
  storageKey: string;
}) {
  const { open: expanded, toggle } = useCollapsible(storageKey);
  if (!plan || plan.totalPayable <= 0) return null;

  const breakdown = plan.breakdown;
  const finance = breakdown?.finance;
  const marketplacePricing = calculateCustomerPricing(provider ?? "granite", providerPremium, instalments);
  const marketplaceFinance = marketplacePricing.finance;

  return (
    <div>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={expanded}
        className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition-colors hover:bg-surface-sunken"
      >
        <span className="text-sm text-content-muted">{label}</span>
        <span className="flex items-center gap-4">
          {marketplaceFinance && (
            <span className="hidden text-xs text-content-subtle sm:inline">
              Deposit {formatCurrency(marketplaceFinance.initialPayment)} · Monthly{" "}
              {formatCurrency(marketplaceFinance.monthlyPayment)} · Total Payable{" "}
            </span>
          )}
          <span className="font-semibold tabular text-content">
            {formatCurrency(marketplaceFinance?.totalPayable ?? plan.totalPayable)}
          </span>
          <ChevronDown
            size={15}
            aria-hidden="true"
            className={cn(
              "text-content-subtle transition-transform duration-200",
              expanded && "rotate-180",
            )}
          />
        </span>
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="space-y-4 bg-surface-sunken px-4 py-4">
              {marketplaceFinance && (
                <Section icon={Tag} title="Our marketplace pricing (Calculated Using Base Premium Total)">
                  <DetailList
                    items={[
                      {
                        label: "Selling price",
                        value: formatCurrency(marketplacePricing.totalSellingPrice),
                      },
                      {
                        label: `Fee in lieu (${formatPercent(marketplacePricing.feeInLieuRate)})`,
                        value: formatCurrency(marketplacePricing.feeInLieuAmount),
                      },
                      {
                        label: `Transaction fee (${formatPercent(marketplaceFinance.transactionFeeRate)})`,
                        value: formatCurrency(marketplaceFinance.transactionFee),
                      },
                      {
                        label: `Deposit (${formatPercent(marketplaceFinance.depositRate)})`,
                        value: formatCurrency(marketplaceFinance.initialPayment),
                      },
                      { label: "Amount financed", value: formatCurrency(marketplaceFinance.loanAmount) },
                      { label: "Monthly payment", value: formatCurrency(marketplaceFinance.monthlyPayment) },
                      { label: "Total payable", value: formatCurrency(marketplaceFinance.totalPayable) },
                    ]}
                  />
                </Section>
              )}

              <Section icon={Receipt} title="Granite returned finance (Untouched)">
                <DetailList
                  items={[
                    { label: "Granite total payable", value: formatCurrency(plan.totalPayable) },
                    { label: "Initial payment", value: formatCurrency(plan.initialPayment) },
                    { label: "Monthly payment", value: formatCurrency(plan.monthlyPayment) },
                    ...(breakdown
                      ? [
                          { label: "Base premium", value: formatCurrency(breakdown.basePremiumTotal) },
                          { label: "Legal fees", value: formatCurrency(breakdown.legalFees) },
                          {
                            label: "Broker arrangement fee",
                            value: formatCurrency(breakdown.brokerArrangementFee),
                          },
                        ]
                      : []),
                  ]}
                />
              </Section>

              {finance && (
                <Section icon={Banknote} title="Granite finance details (Untouched)">
                  <DetailList
                    items={[
                      { label: "Full payment", value: formatCurrency(finance.fullPayment) },
                      { label: "Deposit", value: formatCurrency(finance.deposit) },
                      { label: "Amount financed", value: formatCurrency(finance.amountToFinance) },
                      { label: "Interest", value: formatCurrency(finance.interest) },
                      {
                        label: (
                          <span className="inline-flex items-center gap-1.5">
                            <Percent size={11} aria-hidden="true" /> Interest rate
                          </span>
                        ),
                        value: formatPercent(finance.interestRate),
                      },
                      { label: "Total cost of loan", value: formatCurrency(finance.totalCostOfLoan) },
                      { label: "Monthly repayment", value: formatCurrency(finance.monthlyRepayment) },
                      { label: "Number of months", value: finance.numberOfMonths },
                      {
                        label: (
                          <span className="inline-flex items-center gap-1.5">
                            <Coins size={11} aria-hidden="true" /> Commission
                          </span>
                        ),
                        value: formatCurrency(finance.commissionAmount),
                      },
                    ]}
                  />
                </Section>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function CustomerPricingSummary({
  provider,
  premium,
  className,
}: {
  provider?: string | null;
  premium: number;
  className?: string;
}) {
  const annual = calculateCustomerPricing(provider ?? "", premium);
  const eleven = calculateCustomerPricing(provider ?? "", premium, 11);
  const ten = calculateCustomerPricing(provider ?? "", premium, 10);

  // A label-over-value grid rather than a description list: the dt/dd split
  // leaves too little room for a currency figure once the provider rail has
  // taken its share of the width, and "£1,497.00" wrapping mid-number is worse
  // than a slightly taller block.
  const stats: { label: string; value: string; emphasis?: boolean }[] = [
    { label: "Selling price", value: formatCurrency(annual.totalSellingPrice), emphasis: true },
    { label: `Fee in lieu (${formatPercent(annual.feeInLieuRate)})`, value: formatCurrency(annual.feeInLieuAmount) },
    ...(annual.insurerFeeBeforeCommission > 0
      ? [{ label: "Insurer fee before comm", value: formatCurrency(annual.insurerFeeBeforeCommission) }]
      : []),
    { label: "Premium comm", value: formatCurrency(annual.premiumCommission) },
    { label: "20% deposit", value: formatCurrency(ten.finance?.initialPayment) },
    { label: "10 monthly", value: formatCurrency(ten.finance?.monthlyPayment) },
    { label: "11 monthly", value: formatCurrency(eleven.finance?.monthlyPayment) },
  ];

  return (
    <div className={cn("rounded-lg border border-line bg-surface p-3", className)}>
      <h6 className="mb-2.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-content-subtle">
        <Receipt size={12} aria-hidden="true" />
        Customer pricing
      </h6>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3 xl:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="min-w-0">
            <dt className="truncate text-[11px] uppercase tracking-[0.05em] text-content-subtle">
              {stat.label}
            </dt>
            <dd
              className={cn(
                "mt-0.5 whitespace-nowrap font-semibold tabular text-content",
                stat.emphasis ? "text-base text-gold-700 dark:text-gold-400" : "text-sm",
              )}
            >
              {stat.value}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: LucideIcon;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h6 className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-content-subtle">
        <Icon size={12} aria-hidden="true" />
        {title}
      </h6>
      {children}
    </div>
  );
}

/** A DetailList row for a policy-term list, omitted entirely when there is none. */
function buildTermItems(label: string, values?: string[] | null) {
  const terms = htmlListToPlainText(values);
  if (terms.length === 0) return [];
  return [
    {
      label,
      value: <PolicyTermList terms={terms} />,
      span: 2 as const,
    },
  ];
}

/**
 * A provider reference, which is usually an identifier but is sometimes a URL.
 *
 * Kinetiq returns a full portal retrieve link as its "online reference". Shown
 * as a monospace identifier that is five wrapped lines of query string; shown
 * as a link it is one readable line you can actually click. Only http(s) is
 * linkified — provider data is not trusted to supply a scheme.
 */
function ReferenceValue({ value }: { value: string }) {
  const url = /^https?:\/\//i.test(value.trim()) ? value.trim() : null;

  if (!url) return <CopyableId value={value} truncate={false} />;

  let label = url;
  try {
    const parsed = new URL(url);
    label = parsed.hostname.replace(/^www\./, "");
  } catch {
    // Keep the raw string if it will not parse; the copy button still works.
  }

  return (
    <span className="inline-flex min-w-0 max-w-full items-center gap-1">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        title={url}
        className="inline-flex min-w-0 items-center gap-1 truncate text-sm text-gold-700 underline underline-offset-2 dark:text-gold-400"
      >
        <span className="truncate">{label}</span>
        <ExternalLink size={12} aria-hidden="true" className="shrink-0" />
      </a>
      <CopyButton value={url} label="Copy link" />
    </span>
  );
}

/**
 * Policy conditions and endorsements, made readable.
 *
 * Providers return these as HTML fragments — bold tags around amounts, entity
 * escapes like `&pound;`, and occasionally a leftover `<!-- 1001 -->` build
 * comment. Joining the array with commas produced one unreadable run of markup.
 * Each term now gets its own line, stripped to plain text.
 */
function PolicyTermList({ terms }: { terms: string[] }) {
  if (terms.length === 0) return null;

  if (terms.length === 1) {
    return <span className="leading-relaxed">{terms[0]}</span>;
  }

  return (
    <ul className="list-disc space-y-1 pl-4 leading-relaxed marker:text-content-subtle">
      {terms.map((term, index) => (
        <li key={`${index}-${term.slice(0, 24)}`}>{term}</li>
      ))}
    </ul>
  );
}
